import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { StyledDialog, StyledTextField, StyledButton } from './common/StyledInputs';
import api from '../services/api';

const RecurringPaymentEditModal = ({
  open,
  onClose,
  alert,
  onSuccess,
  onError
}) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (alert?.metadata?.detectedAmount) {
      setAmount(alert.metadata.detectedAmount.toString());
    }
  }, [alert]);

  const handleAmountChange = (event) => {
    setAmount(event.target.value);
  };

  const handleSubmit = async () => {
    if (!alert?.metadata?.budgetId || !alert?.metadata?.categoryName) {
      onError('Missing required data to update category');
      return;
    }

    setLoading(true);
    try {
      // Find the category in the budget and update its amount
      const response = await api.put(`/budgets/${alert.metadata.budgetId}/categories`, {
        categoryName: alert.metadata.categoryName,
        amount: parseFloat(amount),
        isPercentage: false
      });

      if (response.data?.success) {
        onSuccess('Category amount updated successfully!');
        onClose();
      } else {
        throw new Error(response.data?.error || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category amount:', error);
      onError(error.message || 'Failed to update category amount');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const categoryName = alert?.metadata?.categoryName || 'Category';
  const detectedAmount = alert?.metadata?.detectedAmount || 0;
  const averageAmount = alert?.metadata?.averageAmount || 0;

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 0, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Update {categoryName} Amount
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={loading}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Detected Recurring Amount
          </Typography>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            ${detectedAmount.toFixed(2)} per month
          </Typography>
        </Box>

        {averageAmount !== detectedAmount && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Previous Average Amount
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              ${averageAmount.toFixed(2)} per month
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            New Monthly Amount
          </Typography>
          <StyledTextField
            fullWidth
            value={amount}
            onChange={handleAmountChange}
            placeholder="Enter new amount"
            type="number"
            disabled={loading}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This will update the monthly budget amount for {categoryName}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 0, mt: 4 }}>
        <StyledButton variant="outlined" onClick={handleClose} disabled={loading}>
          Cancel
        </StyledButton>
        <StyledButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !amount || parseFloat(amount) <= 0}
        >
          {loading ? 'Updating...' : 'Update Amount'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default RecurringPaymentEditModal;
